/**
 * Subscriptions API Endpoints
 * Stripe subscription management
 */

import apiClient from '../client';
import { Linking, Platform } from 'react-native';
import logger from '../../utils/logger';

// Web app base URL for Stripe redirects
const WEB_APP_URL = __DEV__
  ? 'http://localhost:3000'
  : 'https://app.wealfi.co';

// Price key format: 'premium_monthly', 'premium_yearly', 'pro_monthly', 'pro_yearly'
export type PriceKey = 'premium_monthly' | 'premium_yearly' | 'pro_monthly' | 'pro_yearly';
export type PlanType = 'freemium' | 'premium' | 'pro';
export type BillingInterval = 'month' | 'year';

// Helper to get plan from price key
export const getPlanFromPriceKey = (priceKey: PriceKey): PlanType => {
  if (priceKey.startsWith('pro')) return 'pro';
  if (priceKey.startsWith('premium')) return 'premium';
  return 'freemium';
};

// Helper to get billing interval from price key
export const getBillingInterval = (priceKey: PriceKey): BillingInterval => {
  return priceKey.includes('yearly') ? 'year' : 'month';
};

// Types
export interface SubscriptionLimits {
  messages_per_month: number;
  diagnostics_per_month: number;
  goals_max: number;
  arbitrage_access: boolean;
  investment_simulator: boolean;
  alerts: boolean;
  weekly_checkin: boolean;
}

export interface SubscriptionStatus {
  plan: 'freemium' | 'premium' | 'pro';
  status: string;
  is_premium: boolean;
  is_active: boolean;
  days_remaining: number;
  current_period_end: string | null;
  canceled_at: string | null;
  limits: SubscriptionLimits;
}

export interface CheckoutResponse {
  success: boolean;
  checkout_url: string;
  session_id: string;
}

export interface SubscriptionResponse {
  success: boolean;
  subscription: SubscriptionStatus;
}

export interface PortalResponse {
  success: boolean;
  portal_url: string;
}

export interface PaymentIntentResponse {
  success: boolean;
  session_id: string;  // Checkout session ID (for Embedded Checkout)
  subscription_id?: string;  // Legacy: Stripe subscription ID (deprecated)
  client_secret: string;
  ephemeral_key: string;
  customer_id: string;
  publishable_key: string;
}

export interface CancelResponse {
  success: boolean;
  message: string;
  subscription: {
    plan: string;
    status: string;
    current_period_end: string | null;
  };
}

export interface ConfirmSubscriptionResponse {
  success: boolean;
  subscription: {
    plan: string;
    status: string;
    is_premium: boolean;
    is_active: boolean;
    current_period_end: string | null;
  };
}

export interface ReactivateResponse {
  success: boolean;
  message: string;
  subscription: {
    plan: string;
    status: string;
    is_premium: boolean;
    is_active: boolean;
    current_period_end: string | null;
    canceled_at: string | null;
  };
}

export const subscriptionsApi = {
  /**
   * POST /subscriptions/create-payment-intent
   * Create Payment Intent for in-app subscription payment
   * @param priceKey - Price key like 'premium_monthly', 'premium_yearly', etc.
   */
  createPaymentIntent: async (
    priceKey: PriceKey = 'premium_monthly'
  ): Promise<PaymentIntentResponse> => {
    logger.log('[createPaymentIntent] Creating payment intent for price_key:', priceKey);
    const response = await apiClient.post<PaymentIntentResponse>(
      '/subscriptions/create-payment-intent',
      { price_key: priceKey }
    );
    return response.data;
  },

  /**
   * POST /subscriptions/confirm
   * Confirm subscription status after in-app payment success
   * Syncs the subscription from Stripe to update local status
   */
  confirmSubscription: async (
    subscriptionId: string
  ): Promise<ConfirmSubscriptionResponse> => {
    logger.log('[confirmSubscription] Confirming subscription:', subscriptionId);
    const response = await apiClient.post<ConfirmSubscriptionResponse>(
      '/subscriptions/confirm',
      { subscription_id: subscriptionId }
    );
    return response.data;
  },

  /**
   * POST /subscriptions/checkout
   * Create Stripe Checkout Session for subscription
   * @param priceKey - Price key like 'premium_monthly', 'premium_yearly', etc.
   */
  createCheckout: async (
    priceKey: PriceKey = 'premium_monthly',
    successUrl?: string,
    cancelUrl?: string
  ): Promise<CheckoutResponse> => {
    const payload = {
      price_key: priceKey,
      success_url: successUrl,
      cancel_url: cancelUrl,
    };
    logger.log('[createCheckout] Sending payload:', JSON.stringify(payload, null, 2));

    const response = await apiClient.post<CheckoutResponse>(
      '/subscriptions/checkout',
      payload
    );
    return response.data;
  },

  /**
   * GET /subscriptions/current
   * Get current subscription status
   */
  getCurrentSubscription: async (): Promise<SubscriptionResponse> => {
    const response = await apiClient.get<SubscriptionResponse>(
      '/subscriptions/current'
    );
    return response.data;
  },

  /**
   * DELETE /subscriptions/current
   * Cancel current subscription
   */
  cancelSubscription: async (
    reason?: string,
    immediate: boolean = false
  ): Promise<CancelResponse> => {
    const response = await apiClient.delete<CancelResponse>(
      '/subscriptions/current',
      {
        data: { reason, immediate },
      }
    );
    return response.data;
  },

  /**
   * POST /subscriptions/reactivate
   * Reactivate a canceled subscription (before period end)
   */
  reactivateSubscription: async (): Promise<ReactivateResponse> => {
    logger.log('[reactivateSubscription] Reactivating subscription');
    const response = await apiClient.post<ReactivateResponse>(
      '/subscriptions/reactivate'
    );
    return response.data;
  },

  /**
   * POST /subscriptions/portal
   * Create Stripe Customer Portal session for subscription management
   */
  createPortalSession: async (returnUrl?: string): Promise<PortalResponse> => {
    const response = await apiClient.post<PortalResponse>(
      '/subscriptions/portal',
      {
        return_url: returnUrl,
      }
    );
    return response.data;
  },

  /**
   * Open Stripe Checkout in browser
   * Returns true if checkout was opened successfully
   * @param priceKey - Price key like 'premium_monthly', 'premium_yearly', etc.
   */
  openCheckout: async (
    priceKey: PriceKey = 'premium_monthly'
  ): Promise<boolean> => {
    try {
      // Provide success/cancel URLs for Stripe redirect
      const successUrl = `${WEB_APP_URL}/subscription/success`;
      const cancelUrl = `${WEB_APP_URL}/subscription/cancel`;

      logger.log('[Checkout] Requesting with:', { priceKey, successUrl, cancelUrl });

      const response = await subscriptionsApi.createCheckout(priceKey, successUrl, cancelUrl);

      if (response.success && response.checkout_url) {
        // Open in browser
        const canOpen = await Linking.canOpenURL(response.checkout_url);
        if (canOpen) {
          await Linking.openURL(response.checkout_url);
          return true;
        }
      }
      return false;
    } catch (error: any) {
      logger.error('Error opening checkout:', error);
      // Log the actual server error response
      if (error.response) {
        logger.error('[Checkout] Server error:', error.response.status);
        logger.error('[Checkout] Server error details:', JSON.stringify(error.response.data, null, 2));
      }
      return false;
    }
  },

  /**
   * Open Stripe Customer Portal in browser
   */
  openPortal: async (): Promise<boolean> => {
    try {
      const response = await subscriptionsApi.createPortalSession();

      if (response.success && response.portal_url) {
        const canOpen = await Linking.canOpenURL(response.portal_url);
        if (canOpen) {
          await Linking.openURL(response.portal_url);
          return true;
        }
      }
      return false;
    } catch (error) {
      logger.error('Error opening portal:', error);
      return false;
    }
  },
};
